import {
  ChartPieIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  CalendarIcon,
  UsersIcon,
  DevicePhoneMobileIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import AdminSidebar from "../../../components/AdminSidebar";
import { useState } from "react";
import { getUser } from "../../../utils/storage";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const navigate = useNavigate();
  const user = getUser();

  const { data: statistics = {}, isLoading } = useQuery({
    queryKey: ["dashboard-statistics"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/dashboard/statistics`
      );
      return response.data.data;
    },
    initialData: {
      total_green: 0,
      total_blue: 0,
      total_white: 0,
      total_reserved: 0,
      total_sold: 0,
      total_recycled: 0,
    },
  });

  const handleCardClick = (route) => {
    navigate(route);
    window.scrollTo(0, 0);
  };

  const chartData = {
    labels: [
      "Green",
      "Blue",
      "White",
      "Reserved",
      "Sold",
      "Recycled",
      "Spare Parts",
    ],
    datasets: [
      {
        label: "Device Statistics",
        data: [
          statistics.total_green,
          statistics.total_blue,
          statistics.total_white,
          statistics.total_reserved,
          statistics.total_sold,
          statistics.total_recycled,
          statistics.total_spare_parts,
        ],
        backgroundColor: [
          "rgba(34, 197, 94, 0.5)",
          "rgba(59, 130, 246, 0.5)",
          "rgba(156, 163, 175, 0.5)",
          "rgba(234, 179, 8, 0.5)",
          "rgba(34, 197, 94, 0.5)",
          "rgba(59, 130, 246, 0.5)",
          "rgba(59, 130, 246, 0.5)",
        ],
        borderColor: [
          "rgb(34, 197, 94)",
          "rgb(59, 130, 246)",
          "rgb(156, 163, 175)",
          "rgb(234, 179, 8)",
          "rgb(34, 197, 94)",
          "rgb(59, 130, 246)",
          "rgb(59, 130, 246)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Device Statistics Overview",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <AdminSidebar>
      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-0">
            <h1 className="text-3xl font-semibold leading-3 text-gray-900">
              Dashboard
            </h1>
            <h2 className="text-lg text-gray-800 py-4">
              👋 Hello, <span className="font-bold">{user.name}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 pb-6">
            {[
              {
                title: "Total Green",
                value: statistics.total_green,
                route: "/admin/devices/green-stock",
                color: "bg-green-50 hover:bg-green-100",
                icon: PrinterIcon,
              },
              {
                title: "Total Blue",
                value: statistics.total_blue,
                route: "/admin/devices/blue-stock",
                color: "bg-blue-50 hover:bg-blue-100",
                icon: PrinterIcon,
              },
              {
                title: "Total White",
                value: statistics.total_white,
                route: "/admin/devices/white-stock",
                color: "bg-gray-50 hover:bg-gray-100",
                icon: PrinterIcon,
              },
              {
                title: "Total Reserved",
                value: statistics.total_reserved,
                route: "/admin/stock/reserved",
                color: "bg-yellow-50 hover:bg-yellow-100",
                icon: CalendarIcon,
              },
              {
                title: "Total Sold",
                value: statistics.total_sold,
                route: "/admin/stock/sold",
                color: "bg-green-50 hover:bg-green-100",
                icon: ShoppingCartIcon,
              },
              {
                title: "Total Recycled",
                value: statistics.total_recycled,
                route: "/admin/stock/history/recycled",
                color: "bg-blue-50 hover:bg-blue-100",
                icon: ArrowPathIcon,
              },
              {
                title: "Total Spare Parts",
                value: statistics.total_spare_parts,
                route: "/admin/spare-parts",
                color: "bg-gray-50 hover:bg-gray-100",
                icon: ArrowPathIcon,
              },
            ].map((card) => (
              <div
                key={card.title}
                className={`rounded-2xl shadow p-6 flex flex-col justify-between cursor-pointer transition-all duration-300 ${card.color}`}
                onClick={() => handleCardClick(card.route)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-700">
                    {card.title}
                  </h3>
                  <card.icon className="h-6 w-6 text-gray-500" />
                </div>
                <p className="mt-4 text-3xl font-bold text-bluePrimary">
                  {isLoading ? "..." : card.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
